import { ScreeningContentItem } from '@/app/pages/manage-brief-tabs-screen/data/screening-results.model';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Output, OnDestroy, ElementRef, ViewChild, signal, inject, input, Input } from '@angular/core';
import { NoodleService } from '@/app/services/noodle/noodle.service';
import { ContentReviewResponseV2 } from '@/app/common/general-types/ai-video.dto';
import { Subscription } from 'rxjs';
import { IndividualPostAiReviewComponent } from '@/app/atoms/individual-post-ai-review/individual-post-ai-review.component';

@Component({
    selector: 'scr-screening-details-drawer',
    standalone: true,
    imports: [IndividualPostAiReviewComponent],
    templateUrl: './screening-details-drawer.html',
    styleUrl: './screening-details-drawer.css',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ScreeningDetailsDrawerComponent implements OnDestroy {

    @ViewChild('screeningDetailsDrawerDialog', { static: false }) screeningDetailsDrawerDialog?: ElementRef<HTMLDialogElement>;
    @ViewChild('screeningDetailsDrawerButton', { static: false }) screeningDetailsDrawerButton?: ElementRef<HTMLButtonElement>;

    private dialogCloseHandler?: () => void;
    private readonly subscriptionList = new Subscription();

    @Input() content!: ScreeningContentItem;
    @Input() briefId!: string;
    /** Content cards show the arrow launcher. Other screens open the dialog from their own control. */
    readonly showTrigger = input(true);
    private noodleService = inject(NoodleService);
    /** One dialog per card. A shared id makes every button open the first item. */
    get dialogId(): string {
        const id = this.content?.id ?? 'item';
        return `screening-details-${id.replace(/[^A-Za-z0-9_-]/g, '')}`;
    }

    @Output() close = new EventEmitter<void>();

    screeningResults = signal<ContentReviewResponseV2 | undefined>(undefined);
    readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

    onDrawerOpen(): void {
        this.getScreeningResults(this.content.id, this.briefId);
    }

    /** Opens this item's dialog and loads its screening review. */
    open(): void {
        this.onDrawerOpen();
        const dialog = this.screeningDetailsDrawerDialog?.nativeElement;
        if (dialog && !dialog.open) {
            dialog.showModal();
        }
    }

    getScreeningResults(contentId: string, briefId: string): void {
        this.loadingState.set('loading');
        this.subscriptionList.add(
            this.noodleService.getScreeningResultsDirect(contentId, briefId).subscribe({
                next: (results: ContentReviewResponseV2) => {
                    this.screeningResults.set(results);
                    this.loadingState.set('loaded');
                },
                error: () => {
                    this.screeningResults.set(undefined);
                    this.loadingState.set('error');
                },
            }),
        );
    }

    closeDrawer(): void {
        this.stopPlayingVideo();
        this.screeningDetailsDrawerDialog?.nativeElement?.close();
        this.close.emit();
    }

    /** Pause before the dialog closes so playback does not continue behind it. */
    private stopPlayingVideo(): void {
        const dialog = this.screeningDetailsDrawerDialog?.nativeElement;
        dialog?.querySelectorAll('video').forEach((video) => {
            if (!video.paused) {
                video.pause();
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptionList.unsubscribe();
        if (this.dialogCloseHandler) {
            this.dialogCloseHandler();
        }
    }
}